import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Lock, Loader2, ShieldCheck, MapPin, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { OpportunityCard } from "../components/cards";

const TYPES = ["", "BUYING REQUIREMENT", "DISTRIBUTION OPPORTUNITY", "FUNDING REQUIREMENT", "STRATEGIC PARTNERSHIP", "EXPORT OPPORTUNITY"];
const ALLOWED = ["ACTIVE MEMBER", "APPROVED"];

function DetailRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 py-2 border-b border-slate-800/60 last:border-0">
      <span className="w-28 shrink-0 text-[10px] font-mono uppercase tracking-widest text-slate-500 pt-0.5">{label}</span>
      <span className="text-sm text-slate-200" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function Opportunities() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const type = params.get("type") || "";
  const location = params.get("location") || "";
  const [industry, setIndustry] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const allowed = !!user && (user.role === "admin" || ALLOWED.includes(user.membership_status));

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (type) q.set("type", type);
    if (location) q.set("location", location);
    if (industry) q.set("industry", industry);
    if (verifiedOnly) q.set("verified_only", "true");
    api.get(`/opportunities?${q.toString()}`)
      .then((r) => setOpps(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [type, location, industry, verifiedOnly]);

  const openDetail = (opp) => {
    setSelected(opp);
    setDetail(null);
    setDetailError("");
    if (!allowed) return;
    setDetailLoading(true);
    api.get(`/opportunities/${opp.id}`)
      .then((r) => setDetail(r.data))
      .catch((e) => setDetailError(e.response?.data?.detail || "Unable to load details"))
      .finally(() => setDetailLoading(false));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-14" data-testid="opportunities-page">
      <p className="gold-kicker mb-3">Opportunity Marketplace</p>
      <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-100">BUSINESS OPPORTUNITIES<br />THAT MATCH YOUR BUSINESS.</h1>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="opportunity-type-filters">
        {TYPES.map((t) => (
          <button key={t || "all"} onClick={() => { const p = {}; if (t) p.type = t; if (location) p.location = location; setParams(p); }}
            data-testid={`filter-${(t || "all").toLowerCase().replace(/\s/g, "-")}`}
            className={`text-[11px] font-mono uppercase tracking-wider border rounded-sm px-3 py-1.5 transition-colors ${type === t ? "border-gold text-gold bg-gold/10" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
            {t || "ALL"}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Filter by industry…" data-testid="opportunity-industry-filter" className="input-dark !w-52 !py-2.5" />
        <input value={location} onChange={(e) => { const p = {}; if (type) p.type = type; if (e.target.value) p.location = e.target.value; setParams(p); }} placeholder="Filter by location…" data-testid="opportunity-location-filter" className="input-dark !w-52 !py-2.5" />
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} data-testid="opportunity-verified-filter" className="accent-[#D4AF37]" />
          Verified only
        </label>
      </div>

      {loading ? (
        <p className="mt-12 text-sm font-mono text-slate-500 animate-pulse">Scanning the network for opportunities…</p>
      ) : opps.length === 0 ? (
        <p className="mt-12 text-sm text-slate-500" data-testid="opportunities-empty">No opportunities match these filters. Try widening your criteria.</p>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="opportunities-grid">
          {opps.map((o) => <OpportunityCard key={o.id} opp={o} onView={openDetail} />)}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-navy-950/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="opportunity-detail-modal">
          <div className="card-surface hairline-gold w-full max-w-lg p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{selected.type}</p>
                <h2 className="font-display text-xl font-bold text-slate-100 mt-1" data-testid="detail-title">{selected.looking_for}</h2>
              </div>
              <button onClick={() => setSelected(null)} data-testid="detail-close-btn" className="text-slate-500 hover:text-slate-300" aria-label="Close"><X size={18} /></button>
            </div>

            {!user && (
              <div className="text-center py-6" data-testid="detail-locked-guest">
                <Lock size={32} className="text-gold mx-auto mb-4" />
                <p className="text-sm text-slate-300 font-semibold">Members-only opportunity</p>
                <p className="text-xs text-slate-500 mt-2 max-w-xs mx-auto">Full opportunity details are available only to verified 10XGLOBAL members and admins.</p>
                <div className="mt-6 flex justify-center gap-3">
                  <Link to="/login" data-testid="detail-login-btn" className="btn-outline !px-6 !py-2.5 text-xs">SIGN IN</Link>
                  <Link to="/register" data-testid="detail-register-btn" className="btn-gold !px-6 !py-2.5 text-xs">JOIN NOW</Link>
                </div>
              </div>
            )}

            {user && !allowed && (
              <div className="text-center py-6" data-testid="detail-locked-member">
                <ShieldCheck size={32} className="text-gold mx-auto mb-4" />
                <p className="text-sm text-slate-300 font-semibold">Verified membership required</p>
                <p className="text-xs text-slate-500 mt-2 max-w-xs mx-auto">Your account ({user.membership_status || "REGISTERED"}) needs an active verified membership to view full opportunity details.</p>
                <Link to="/membership" data-testid="detail-membership-btn" className="btn-gold inline-block mt-6 !px-6 !py-2.5 text-xs">VIEW MEMBERSHIP PLANS</Link>
              </div>
            )}

            {allowed && detailLoading && (
              <div className="py-10 flex justify-center" data-testid="detail-loading"><Loader2 size={22} className="animate-spin text-gold" /></div>
            )}
            {allowed && detailError && <p className="text-xs text-red-400 py-4" data-testid="detail-error">{detailError}</p>}
            {allowed && detail && (
              <div data-testid="detail-content">
                <DetailRow label="Organisation" value={detail.org} testid="detail-org" />
                <DetailRow label="Location" value={detail.location} testid="detail-location" />
                <DetailRow label="Budget" value={detail.budget} testid="detail-budget" />
                <DetailRow label="Industry" value={detail.industry} testid="detail-industry" />
                <DetailRow label="Status" value={`${detail.status} · ${detail.posted}`} testid="detail-status" />
                {detail.why && (
                  <p className="mt-4 text-xs text-slate-400 leading-relaxed border-l-2 border-gold/40 pl-3" data-testid="detail-why">{detail.why}</p>
                )}
                {detail.verified && <p className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-techblue-light"><ShieldCheck size={12} /> Business Verified</p>}
                {user?.role !== "admin" && (
                  <p className="mt-4 flex items-start gap-2 text-[11px] text-slate-500" data-testid="detail-org-note">
                    <Lock size={11} className="text-gold mt-0.5 shrink-0" /> Company details are visible to admins only — shared with you after an admin-reviewed match.
                  </p>
                )}
                <div className="mt-6 flex items-center gap-2 text-xs text-slate-500"><MapPin size={12} className="text-gold" /> Respond via your dashboard connections after reviewing the fit.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
